import { useEffect } from 'react'
import { getEnvironment } from '../../auth/config'
import type { User } from '../../auth/types'
import type { GainsightAccountFields, GainsightGlobalContext, GainsightUserFields } from './types'

type Environment = 'devlocal' | 'dev' | 'staging' | 'prod'

const getApiKeyForEnv = (env: Environment): string => {
  switch (env) {
    case 'dev':
      return 'AP-QQO1AGBIFHP2-2-3'
    case 'staging':
      return 'AP-QQO1AGBIFHP2-2-2'
    case 'prod':
      return 'AP-QQO1AGBIFHP2-2'
    case 'devlocal':
    default:
      return 'AP-QQO1AGBIFHP2-2-4'
  }
}

const DEFAULT_SCRIPT_ID = 'analytics-gainsight'

const getEmbedScript = (id: string) => `
  (function(n,t,a,e,co){var i="aptrinsic";n[i]=n[i]||function(){
      (n[i].q=n[i].q||[]).push(arguments)},n[i].p=e;n[i].c=co;
    var r=t.createElement("script");r.async=!0,r.src=a+"?a="+e;
    var c=t.getElementsByTagName("script")[0];c.parentNode.insertBefore(r,c)
  })(window,document,"https://web-sdk.aptrinsic.com/api/aptrinsic.js","${id}");
`

/**
 * Initialize Gainsight analytics script
 */
export const initGainsight = (env: Environment) => {
  const apiKey = getApiKeyForEnv(env)

  if (!apiKey || apiKey.includes('PLACEHOLDER')) {
    console.warn('Gainsight API key not configured for environment:', env)
    return
  }

  if (!document) {
    return
  }

  const hasScript = !!document.getElementById(DEFAULT_SCRIPT_ID)
  if (hasScript) {
    return
  }

  const script = document.createElement('script')
  script.innerHTML = getEmbedScript(apiKey)
  script.id = DEFAULT_SCRIPT_ID
  script.async = true

  document.body.appendChild(script)
}

/**
 * Hook to manage Gainsight user identification and context
 */
export const useGainsight = (user: User | null) => {
  const environment = getEnvironment()

  // Identify user with Gainsight
  useEffect(() => {
    if (!user) return

    try {
      const userFields: GainsightUserFields = {
        id: user.id,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        shortName:
          user.name || user.preferred_username || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email,
      }

      // For DD Summarizer, we use the user ID as the account ID since there's no business concept
      const accountFields: GainsightAccountFields = {
        id: user.id,
      }

      window.aptrinsic?.('identify', userFields, accountFields)
    } catch (e) {
      // Silently fail on any gainsight internal errors - we would rather continue and this get logged to other bug
      // handlers than for an error in gainsight to impact the user flow.
      console.error('Gainsight identify error:', e)
    }
  }, [user])

  // Set global context
  useEffect(() => {
    if (!user) return

    try {
      const globalContext: GainsightGlobalContext = {
        platform: 'web',
        app: 'dd-summarizer',
        userEmail: user.email,
        environment,
      }

      window.aptrinsic?.('set', 'globalContext', globalContext)
    } catch (e) {
      // Silently fail on any gainsight internal errors - we would rather continue and this get logged to other bug
      // handlers than for an error in gainsight to impact the user flow.
      console.error('Gainsight globalContext error:', e)
    }
  }, [user, environment])

  return null
}
